/**
 * consoleGuard.ts
 *
 * Must be imported before anything else in index.tsx.
 *
 * Development : all console output passes through unchanged.
 * Production  : console.log / console.info are silenced completely.
 *               console.warn / console.error still fire but have every
 *               argument scrubbed of sensitive patterns before printing.
 */

const IS_PROD = process.env.NODE_ENV === 'production';

// ── Patterns that must never appear in the console in production ──────────────

/** Matches a JWT (three base64url segments separated by dots). */
const JWT_RE = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;

/** Matches "access_token", "refresh_token", "auth_token" followed by a value. */
const TOKEN_KV_RE = /(access_token|refresh_token|auth_token|transfer_code|code)\s*[=:]\s*\S+/gi;

/** Matches "Bearer <anything>" */
const BEARER_RE = /Bearer\s+\S+/gi;

/** Matches explicit cookie payload strings. */
const COOKIE_RE = /auth_transfer\s*=\s*[^;]+/gi;

/** Matches numeric IDs in known sensitive param names. */
const ID_PARAM_RE = /(workspace_id|user_id|userId|workspaceId)\s*[=:]\s*\d+/gi;

/** Matches /media/ file paths (screenshot / recording / voice URLs). */
const MEDIA_PATH_RE = /\/media\/[^\s"')]+/g;

/** Matches subdomain storage log lines (the full string). */
const SUBDOMAIN_LOG_RE = /subdomain\s+(stored|mismatch|cleared|verified|corrected|ownership)/i;

const SENSITIVE_PATTERNS: RegExp[] = [
  JWT_RE,
  TOKEN_KV_RE,
  BEARER_RE,
  COOKIE_RE,
  ID_PARAM_RE,
  MEDIA_PATH_RE,
];

// ── Redaction helpers ─────────────────────────────────────────────────────────

function redactString(s: string): string {
  let out = s;
  for (const re of SENSITIVE_PATTERNS) {
    re.lastIndex = 0;
    out = out.replace(re, '[REDACTED]');
  }
  return out;
}

function sanitize(arg: unknown): unknown {
  if (typeof arg === 'string') {
    return redactString(arg);
  }
  if (arg && typeof arg === 'object') {
    try {
      const raw = JSON.stringify(arg);
      const redacted = redactString(raw);
      return raw === redacted ? arg : JSON.parse(redacted);
    } catch {
      return arg;
    }
  }
  return arg;
}

function isSensitiveMessage(args: unknown[]): boolean {
  return args.some(
    (a) =>
      typeof a === 'string' &&
      (SUBDOMAIN_LOG_RE.test(a) ||
        /token(s)?\s+(restored|refresh|expired|invalid|stored|cleared)/i.test(a))
  );
}

// ── Override ──────────────────────────────────────────────────────────────────

export function initConsoleGuard(): void {
  if (!IS_PROD) return; // dev: leave console untouched

  const _log   = console.log.bind(console);   // kept only for potential future allow-list
  const _warn  = console.warn.bind(console);
  const _error = console.error.bind(console);

  // Silence log + info entirely in production
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  console.log  = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  console.info = () => {};

  // Warn: suppress sensitive messages, redact the rest
  console.warn = (...args: unknown[]) => {
    if (isSensitiveMessage(args)) return;
    _warn(...args.map(sanitize));
  };

  // Error: always show (needed for crash diagnosis), but redact values
  console.error = (...args: unknown[]) => {
    if (isSensitiveMessage(args)) return;
    _error(...args.map(sanitize));
  };
}
